// **React Imports
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'

// **MUI Imports
import { Box } from '@mui/material'

// **Third party Imports
import debounce from 'lodash.debounce'

const DEFAULT_SIZE = {
  width: '20%',
  height: '25%'
}

const ZOOMED_SIZE = {
  width: '35%',
  height: '40%'
}

const DraggableWindow = ({
  children,
  offsetX = 10,
  offsetY = 10,
  zoomedOffsetX = 10,
  zoomedOffsetY = 10,
  className = '',
  modalStyle = {},
  draggableProps = {},
  zoomed
}) => {
  const dragStartPosition = useRef({ x: 0, y: 0 })
  const position = useRef({ x: 0, y: 0 })
  const boxRef = useRef(null)
  const userResized = useRef(false)

  const [isDragging, setIsDragging] = useState(false)

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true)
    dragStartPosition.current = {
      x: e.clientX - position.current.x,
      y: e.clientY - position.current.y
    }
  }, [])

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        const box = boxRef.current.getBoundingClientRect()

        const newX = e.clientX - dragStartPosition.current.x
        const newY = e.clientY - dragStartPosition.current.y

        const maxX = window.innerWidth - box.width
        const maxY = window.innerHeight - box.height - 6

        position.current = {
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        }

        if (boxRef.current) {
          boxRef.current.style.left = `${position.current.x}px`
          boxRef.current.style.top = `${position.current.y}px`
        }
      }
    },
    [isDragging]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const getOffsets = useCallback(() => {
    return zoomed
      ? { x: zoomedOffsetX, y: zoomedOffsetY }
      : { x: offsetX, y: offsetY }
  }, [zoomed, offsetX, offsetY, zoomedOffsetX, zoomedOffsetY])

  const resolveSize = useCallback((value, total) => {
    if (typeof value === 'string' && value.endsWith('%')) {
      return (parseFloat(value) / 100) * total
    }
    return value
  }, [])

  const moveToBottomRight = useCallback(
    (width, height) => {
      if (!boxRef.current) return

      const { x: offX, y: offY } = getOffsets()

      const resolvedWidth = resolveSize(width, window.innerWidth)
      const resolvedHeight = resolveSize(height, window.innerHeight)

      position.current = {
        x: Math.max(0, window.innerWidth - resolvedWidth - offX),
        y: Math.max(0, window.innerHeight - resolvedHeight - offY)
      }

      boxRef.current.style.left = `${position.current.x}px`
      boxRef.current.style.top = `${position.current.y}px`
    },
    [getOffsets, resolveSize]
  )

  const handleWindowResize = useCallback(() => {
    if (!boxRef.current) return

    const box = boxRef.current.getBoundingClientRect()

    position.current = {
      x: Math.max(
        0,
        Math.min(position.current.x, window.innerWidth - box.width)
      ),
      y: Math.max(
        0,
        Math.min(position.current.y, window.innerHeight - box.height - 6)
      )
    }

    boxRef.current.style.left = `${position.current.x}px`
    boxRef.current.style.top = `${position.current.y}px`
  }, [])

  // eslint-disable-next-line
  const debounceResizing = useCallback(debounce(handleWindowResize, 300), [
    handleWindowResize
  ])

  useEffect(() => {
    if (!boxRef.current || !zoomed) return

    const observer = new ResizeObserver(() => {
      userResized.current = true
    })

    observer.observe(boxRef.current)
    return () => observer.disconnect()
  }, [zoomed])

  // effect to handle resizing
  useEffect(() => {
    window.addEventListener('resize', debounceResizing)
    return () => {
      window.removeEventListener('resize', debounceResizing)
    }
  }, [debounceResizing])

  // effect to zooming the feed
  useEffect(() => {
    if (!boxRef.current || zoomed == null) return

    userResized.current = false

    const { width, height } = zoomed ? ZOOMED_SIZE : DEFAULT_SIZE

    boxRef.current.style.width =
      typeof width === 'number' ? `${width}px` : width
    boxRef.current.style.height =
      typeof height === 'number' ? `${height}px` : height

    moveToBottomRight(width, height)
  }, [zoomed, moveToBottomRight])

  // effect to position the feed initiallly
  useEffect(() => {
    if (!boxRef.current) return

    const box = boxRef.current.getBoundingClientRect()
    moveToBottomRight(box.width, box.height)
  }, [offsetX, offsetY, zoomedOffsetX, zoomedOffsetY, moveToBottomRight])

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'absolute',
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: 1250,
        userSelect: isDragging ? 'none' : 'auto',
        pointerEvents: 'auto',
        ...modalStyle
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      className={`${className || ''}`}
      {...draggableProps}
    >
      {children}
    </Box>
  )
}

export default memo(DraggableWindow)
